import { FormControlLabel } from "@mui/material";
import CalendarCheckFillIcon from "remixicon-react/CalendarCheckFillIcon";
import dateformat from "dateformat";
import Switch from "@mui/material/Switch";
import { createUseStyles } from "react-jss";
import { useQuery, useQueryClient } from "react-query";
import { useState } from "react";
import { useSnackbar } from "react-simple-snackbar";
import {
  getCompanyTypeLabel,
  getCompanyTypeColor,
} from "$utils/typeConstants/Company";
import useCompany from "$hooks/useCompany";
import { DEFAULT_DATE_FORMAT } from "$utils/utils";
import { theme } from "$utils/theme/bataillog";
import { CompanyServices } from "$services";
import { CacheInvalidationAction } from "$utils/cache";
import { snackbarErrorOptions } from "$utils/SnackbarOptions";

const COMPANY_DEACTIVATION_BLOCKED_MESSAGE =
  "Cette entreprise ne peut pas être désactivée car des EDB ou préparations sont en cours.";

const isCompanyDeactivationBlockedError = (message?: string | null) =>
  !!message?.includes("Impossible de désactiver cette company");

type Props = {
  company: any;
};

function Header({ company }: Props) {
  const { companyTypes } = company;
  const badgeClasses = useStyles();
  const { classes } = useCompany();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [openSnackbarError] = useSnackbar(snackbarErrorOptions);
  const queryClient = useQueryClient();

  const { data: parentCompany } = useQuery(
    [CacheInvalidationAction.GET_COMPANY_BY_ID, company.agencyOf?._id],
    () => CompanyServices.getCompanyById(company.agencyOf?._id),
    {
      enabled: !!company.agencyOf?._id,
      cacheTime: 60 * 1000,
    }
  );

  const toggleActiveCompany = async () => {
    setIsLoading(true);
    try {
      const result = await CompanyServices.editCompany(company._id, {
        ...company,
        isActive: !company?.isActive,
      });

      if (result?.error) {
        openSnackbarError(
          isCompanyDeactivationBlockedError(result.error)
            ? COMPANY_DEACTIVATION_BLOCKED_MESSAGE
            : result.error
        );
        return;
      }

      await queryClient.invalidateQueries([
        CacheInvalidationAction.GET_COMPANY_BY_ID,
        company._id,
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className={classes.header}>
        {companyTypes.map((ct) => (
          <span
            key={ct}
            className={badgeClasses.companyTypeBadge}
            style={{ background: getCompanyTypeColor(ct, theme) }}
          >
            {getCompanyTypeLabel(ct)}
          </span>
        ))}

        <div className={classes.calendar}>
          <CalendarCheckFillIcon className={classes.calendarIcon} />
          Créée le{" "}
          {company?.createdAt &&
            dateformat(new Date(company?.createdAt), DEFAULT_DATE_FORMAT)}
        </div>
        <FormControlLabel
          className={classes.switchButton}
          control={
            <Switch
              disabled={
                (parentCompany && !parentCompany?.isActive) || isLoading
              }
              checked={company?.isActive} // we use the negation everywhere bcs it's the isDisable, in company we are using isActive so we don't use the negation
              onChange={toggleActiveCompany}
              name="activeCompany"
              color="primary"
            />
          }
          label="Entreprise active"
        />
      </div>
    </>
  );
}

export default Header;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useStyles = createUseStyles(() => ({
  companyTypeBadge: {
    marginRight: 16,
    color: "white",
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600,
  },
}));
